import React from 'react';
import { Compass, ArrowRight, LayoutDashboard } from 'lucide-react';
import { Reveal } from '../Reveal';
import { DeviceFrame } from './DeviceFrame';
import { Eyebrow } from './SectionBand';
import { BookImage } from './BookImage';

/**
 * Native HTML/CSS illustrative interface reused as the book-to-working-system
 * visual. It depicts patterns that exist in the real prototype (diagnostic,
 * readout, next move, reader signal, fitting support, author context) with
 * generic, non-attributed content — not a real client screenshot.
 */
function IllustrativeInterface() {
  return <div className="flex flex-col" data-id="element-941" data-mp="14aadsgm9lwiv">
      <div className="flex items-center justify-between gap-4 border-b border-gray-100 bg-paper px-5 py-3" data-id="element-942" data-mp="149h5hb7nqyym">
        <span className="font-serif text-sm font-semibold text-navy" data-id="element-943" data-mp="10a8ci8kfz544">
          Reader-to-Revenue
        </span>
        <div className="hidden items-center gap-2 text-[0.7rem] font-medium text-text-muted sm:flex" data-id="element-944" data-mp="1e8zlxhod02lx">
          <span data-id="element-945" data-mp="19bg6iefxdjse">Diagnostic</span>
          <span className="text-gray-300" data-id="element-946" data-mp="2pedz50tpct06">›</span>
          <span className="rounded-full bg-seaglass/15 px-2 py-0.5 text-seaglass" data-id="element-947" data-mp="2pfp24yfbpwq8">
            Readout
          </span>
          <span className="text-gray-300" data-id="element-948" data-mp="2ufbgjb6w1j0m">›</span>
          <span data-id="element-949" data-mp="2zc25yp4l1qyp">Next move</span>
        </div>
      </div>

      <div className="grid gap-4 p-5 md:grid-cols-[1.4fr_1fr] md:p-6" data-id="element-950" data-mp="2zeof2t74gej1">
        <div data-id="element-951" data-mp="33fgmdgwfo9co">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-952" data-mp="33bssuhqznmxy">
            Personalized readout
          </p>
          <h4 className="mt-2 font-serif text-xl leading-snug text-navy md:text-2xl" data-id="element-953" data-mp="3dbjmkh7q5xwz">
            Here is where the reader is getting stuck
          </h4>
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-gray-100 bg-paper p-4" data-id="element-954" data-mp="3i4wbytk8a8qs">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-seaglass/15 text-seaglass" data-id="element-955" data-mp="3n3z45df40khc">
              <Compass className="h-4 w-4" data-id="element-956" data-mp="3w2b4i75s2wmz" />
            </span>
            <p className="text-sm leading-relaxed text-text" data-id="element-957" data-mp="3w0hxgqr96aeg">
              Recognizes the problem in the book — unsure what to do with it
              next.
            </p>
          </div>

          <div className="mt-4 rounded-xl border border-sand/50 bg-sand/10 p-4" data-id="element-958" data-mp="1s0kpfr7bvyze">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-sand-dark" data-id="element-959" data-mp="25qwhxr7trzr2">
              Recommended next move
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-navy" data-id="element-960" data-mp="2foapf1brte78">
              Turn one idea from the book into a focused action this week.
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#d4a574] px-3 py-1.5 text-xs font-bold text-white" data-id="element-961" data-mp="2aoy10c593z2j">
              Build my next move <ArrowRight className="h-3.5 w-3.5" data-id="element-962" data-mp="2jpe43ki3146v" />
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-4" data-id="element-963" data-mp="uafzrq4vz50i">
          <div className="rounded-xl border border-gray-100 bg-paper p-4" data-id="element-964" data-mp="ub7wj4a7z8sd">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-text-muted" data-id="element-965" data-mp="pd6d2i8vt7gd">
              What the reader signaled
            </p>
            <div className="mt-2.5 flex flex-wrap gap-1.5" data-id="element-966" data-mp="35ybba4zk9jew">
              {['Interested', 'Wants a framework', 'Not ready for a call'].map(chip => <span key={chip} className="rounded-full border border-seaglass/30 bg-white px-2.5 py-1 text-[0.7rem] font-medium text-navy" data-id="element-967" data-mp="2s5v730uxbfce">
                    {chip}
                  </span>)}
            </div>
          </div>
          <div className="rounded-xl border border-gray-100 bg-paper p-4" data-id="element-968" data-mp="2i75pzaaupyx7">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-text-muted" data-id="element-969" data-mp="2n8uxifyv7ugj">
              Fitting support
            </p>
            <div className="mt-2.5 space-y-1.5" data-id="element-970" data-mp="29aevv3fv0u59">
              {['Apply it yourself', 'Move toward guided help'].map(row => <div key={row} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs font-medium text-navy ring-1 ring-gray-100" data-id="element-971" data-mp="7gp9e2c1qdwo">
                  {row}
                  <ArrowRight className="h-3 w-3 text-seaglass" data-id="element-972" data-mp="bfy53cqncluv" />
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-navy px-5 py-4 md:px-6" data-id="element-973" data-mp="3dxo6gnun9f7r">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-974" data-mp="38ykyovna4uw3">
          <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-seaglass-light" data-id="element-975" data-mp="390xotw36ybk8">
            <LayoutDashboard className="h-3.5 w-3.5" data-id="element-976" data-mp="2zyojdkms58dp" /> Author context
          </span>
          {[['Known reader', 'saved result'], ['Topic of interest', 'applying the framework'], ['Suggested follow-up', 'send the guide']].map(([k, v]) => <span key={k} className="text-[0.72rem] text-paper/70" data-id="element-977" data-mp="2q94yk9fib4g7">
              <span className="text-paper/45" data-id="element-978" data-mp="2l9s9rj52rnaz">{k}: </span>
              {v}
            </span>)}
        </div>
      </div>
    </div>;
}
export function Solution() {
  return <section className="bg-navy px-6 pt-20 pb-14 text-paper md:px-10 md:pt-28 md:pb-16" data-id="element-979" data-mp="2gc09a0qd6sv6">
      <div className="mx-auto max-w-6xl" data-id="element-980" data-mp="2caorghdqfbxj">
        <div className="max-w-3xl" data-id="element-981" data-mp="rr2urx8zozrj">
          <Reveal data-id="element-982" data-mp="rq02plzxkra1">
            <Eyebrow data-id="element-983" data-mp="rsm9vkiwt4rq">The Reader-to-Revenue Build</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-984" data-mp="vr3kie1jgxdw">
              We build what comes next.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-paper/70" data-id="element-985" data-mp="10peucoru37w4">
              Author Leverage takes the ideas, frameworks, stories, language, and
              offers already inside your book and builds one connected
              Reader-to-Revenue System around them.
            </p>
            <p className="mt-8 font-serif text-2xl leading-snug text-seaglass-light md:text-3xl" data-id="element-986" data-mp="czkedyc7x37n">
              One done-for-you service.
            </p>
            <div className="mt-6 space-y-3 text-lg leading-relaxed text-paper/80 md:text-xl" data-id="element-987" data-mp="msi3inbptqf8">
              <p data-id="element-988" data-mp="3li8fc4pkbzi4">The reader experience is the mechanism.</p>
              <p data-id="element-989" data-mp="3qgjsopf2gs53">Author leverage is the outcome.</p>
              <p data-id="element-990" data-mp="3qf8owplmfkdz">
                Revenue architecture and sales enablement make that leverage
                commercially useful.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-991" data-mp="3edh3xurqm8e0">
          <div className="mt-14 grid items-center gap-8 lg:grid-cols-[0.85fr_auto_1.4fr] lg:gap-6" data-id="element-992" data-mp="3ec605uyal0mw">
            <div className="flex justify-center" data-id="element-993" data-mp="3s7rkhotkyw8z">
              <BookImage className="h-56 md:h-72" data-id="element-994" data-mp="3n6aq8c5lq81d" />
            </div>
            <div className="flex flex-col items-center gap-2 text-center" data-id="element-995" data-mp="2zgzrf07tanpv">
              <ArrowRight className="hidden h-6 w-6 text-seaglass-light lg:block" data-id="element-996" data-mp="2zj2jdpsiyqx8" />
              <ArrowRight className="h-6 w-6 rotate-90 text-seaglass-light lg:hidden" data-id="element-997" data-mp="2vh7igmeiafjz" />
            </div>
            <div data-id="element-998" data-mp="39ggzfoajddp0">
              <DeviceFrame variant="browser" url="reader.authorleverage.com" data-id="element-999" data-mp="39ct5wp53craa">
                <IllustrativeInterface data-id="element-1000" data-mp="34erm21zucnxr" />
              </DeviceFrame>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-1001" data-mp="oqrmjgte81yy">
          <p className="mt-8 text-center text-base font-medium text-paper/60 md:text-lg" data-id="element-1002" data-mp="jqdbrr0eqdx8">
            Illustrative Reader-to-Revenue system view. Real working prototype
            shown below.
          </p>
        </Reveal>
      </div>
    </section>;
}